import {
  Button,
  Modal,
  desktopFirstMediaQuery,
  desktopFirstQuery,
  useShowNotification,
} from '@inflearn/ds-react';
import type { EditorFeature } from '@inflearn/editor';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';

import { useBeforeUnloadEvent } from '../../../../hooks/useBeforeUnloadEvent';
import { useUpdateIntroduce } from '../../../../hooks/useUpdateIntroduce';
import Editor from '../../../common/Editor';

export default function IntroductionEditorModal({
  opened,
  onClose,
  editorInitialValue = '',
  userId,
}: {
  opened: boolean;
  onClose: () => void;
  editorInitialValue: string;
  userId: number;
}) {
  const { t } = useTranslation();
  const editorRef = useRef<EditorFeature>(null);
  const [isIntroductionUpdated, setIsIntroductionUpdated] = useState(false);

  const { mutateAsync: updateIntroduce, isLoading } = useUpdateIntroduce(userId);
  const { showNotification } = useShowNotification();

  const isMobile = useMediaQuery(desktopFirstQuery.mobile);
  const editorHeight = isMobile ? '300px' : '480px';

  const handleClose = () => {
    setIsIntroductionUpdated(false);
    onClose();
  };

  const handleSave = async () => {
    if (!editorRef.current) {
      console.error(t('editor 를 찾을 수 없습니다.'));

      return;
    }

    const newIntroduce = await editorRef.current.getSubmitContent();

    await updateIntroduce(
      { body: newIntroduce },
      {
        onSuccess: () => {
          showNotification({
            type: 'success',
            title: t('프로필 소개가 저장되었어요.'),
          });
          handleClose();
        },
        onError: () => {
          showNotification({
            type: 'error',
            title: t('프로필 소개 저장에 실패했어요.'),
            message: t('같은 오류가 계속 발생한다면 서비스에 문의해주세요.'),
          });
        },
      },
    );
  };

  const isNotIntroductionUpdated = !isIntroductionUpdated;

  useBeforeUnloadEvent(isNotIntroductionUpdated);

  return (
    <Modal.Root
      opened={opened}
      onClose={handleClose}
      variant="confirmation"
      size="xl"
      closeOnClickOutside={false}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>{t('프로필 소개')}</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Editor
            ref={editorRef}
            initialValue={editorInitialValue}
            onChange={() => setIsIntroductionUpdated(true)}
            height={editorHeight}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="md"
            radius="md"
            variant="default"
            onClick={handleClose}
            css={{
              [desktopFirstMediaQuery.mobile]: {
                flex: 1,
              },
            }}
          >
            {t('취소')}
          </Button>
          <Button
            loading={isLoading}
            size="md"
            radius="md"
            css={{
              [desktopFirstMediaQuery.mobile]: {
                flex: 1,
              },
            }}
            onClick={handleSave}
          >
            {t('저장')}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
